import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import { Dispatch } from 'redux';
import FileServices from 'services/FileServices';
import { CacheTypes } from 'modules/types/mobileCaching';
import {
  socialHistoryDownloadingSelector,
  socialHistoryRequestStateSelector
} from 'store/medicalHistory/socialHistory/selectors';
import { messageReply, messageShare } from 'store/messaging/actions';
import { currentConsumerPcpDoctorsSelector } from 'store/myDoctors/selectors';
import { RootState, Thunk } from 'store/types';
import { oc } from 'ts-optchain';
import { DFDHeadersObject, DispatcherResource, DispatcherEndpoint } from 'modules/types/common';
import { DFDHeaders } from 'serverTypes/authentication';
import { getMedicalHistoryEndpointGenerator } from '../common';
import { TransmitSources } from '../../transmit';
import { SocialHistory, SocialHistoryCategories } from '../types';

/**
 * Get Social History
 */
export const FETCH_SOCIAL_HISTORY_CACHE_SUCCESS =
  'SOCIAL_HISTORY.FETCH_SOCIAL_HISTORY_CACHE_SUCCESS';

export const FETCH_SOCIAL_HISTORY = 'SOCIAL_HISTORY.FETCH_SOCIAL_HISTORY';
export const FETCH_SOCIAL_HISTORY_SUCCESS = 'SOCIAL_HISTORY.FETCH_SOCIAL_HISTORY_SUCCESS';
export const FETCH_SOCIAL_HISTORY_FAIL = 'SOCIAL_HISTORY.FETCH_SOCIAL_HISTORY_FAIL';
const getEndpoint = getMedicalHistoryEndpointGenerator(DispatcherResource.SOCIAL_HISTORY);

export const fetchSocialHistory = (useRequestState = true) => ({
  type: FETCH_SOCIAL_HISTORY,
  payload: {
    useRequestState,
    requestStateSelector: socialHistoryRequestStateSelector,
    cacheType: CacheTypes.SOCIAL_HISTORY,
    cacheRetrievalSuccessAction: FETCH_SOCIAL_HISTORY_CACHE_SUCCESS,
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getEndpoint()
    }
  }
});

// Start of Medical History Reply:
export const createReplyFromHistory = (
  alcohol: SocialHistory[],
  tobacco: SocialHistory[],
  substanceUse: SocialHistory[],
  sexualGender: SocialHistory[],
  homeEnvironment: SocialHistory[],
  employmentSchool: SocialHistory[]
) => {
  const alcoholText =
    alcohol.length >= 1
      ? alcohol
          .map(
            a =>
              `\n${a.name} was issued ${a.dateIssued} to Patient ID: ${a.patientId}\nDetails: ${a.description}\nStatus: ${a.status}, ${a.text}\n(Code: ${a.code} last updated: ${a.lastUpdated})`
          )
          .join(`,\n`)
      : '\nNo recorded alcohol history\n';

  const tobaccoText =
    tobacco.length >= 1
      ? tobacco
          .map(
            t =>
              `\n${t.name} was issued ${t.dateIssued} to Patient ID: ${t.patientId}\nDetails: ${t.description}\nStatus: ${t.status}, ${t.text}\n(Code: ${t.code} last updated: ${t.lastUpdated})`
          )
          .join(`,\n`)
      : '\nNo recorded tobacco history\n';

  const substanceUseText =
    substanceUse.length >= 1
      ? substanceUse
          .map(
            su =>
              `\n${su.name} was issued ${su.dateIssued} to Patient ID: ${su.patientId}\nDetails: ${su.description}\nStatus: ${su.status}, ${su.text}\n(Code: ${su.code} last updated: ${su.lastUpdated})`
          )
          .join(`,\n`)
      : '\nNo recorded substance use history\n';

  const sexualGenderText =
    sexualGender.length >= 1
      ? sexualGender
          .map(
            sg =>
              `\n${sg.name} was issued ${sg.dateIssued} to Patient ID: ${sg.patientId}\nCategory: ${sg.category}\nStatus: ${sg.status}, ${sg.text}\n(Last updated on ${sg.lastUpdated})`
          )
          .join(`,\n`)
      : '\nNo recorded sexual gender history\n';

  const homeEnvironmentText =
    homeEnvironment.length >= 1
      ? homeEnvironment
          .map(
            he =>
              `\n${he.name}: ${he.text}\nStatus: ${he.status}, issued on ${he.dateIssued}\nCategory: ${he.category}, Patient ID: ${he.patientId}\n(Last updated: ${he.lastUpdated})`
          )
          .join(`,\n`)
      : '\nNo recorded home environment history\n';

  const employmentSchoolText =
    employmentSchool.length >= 1
      ? employmentSchool
          .map(
            es =>
              `\n${es.name}: ${es.text}\nStatus: ${es.status}, issued on ${es.dateIssued}\nCategory: ${es.category}, Patient ID: ${es.patientId}\n(Last updated: ${es.lastUpdated})`
          )
          .join(`,\n`)
      : '\nNo recorded employment or school history\n';

  const replyText = `Social History\n\nAlcohol: \n${alcoholText}\n\nTobacco: \n${tobaccoText}\n\nSubstance Use: \n${substanceUseText}\n\nSexual Gender: \n${sexualGenderText}\n\nHome Environment: \n${homeEnvironmentText}\n\nEmployment School: \n${employmentSchoolText}\n`;
  return replyText;
};

export const replyToSocialHistory = (
  socialHistory: SocialHistoryCategories,
  isShare = false,
  returnBackTo?: string
): Thunk => (dispatch, getState) => {
  const consumerPcpList = currentConsumerPcpDoctorsSelector(getState());

  if (!socialHistory) {
    return;
  }

  const alcohol = oc(socialHistory).alcohol([]);
  const tobacco = oc(socialHistory).tobacco([]);
  const substanceUse = oc(socialHistory).substanceUse([]);
  const sexualGender = oc(socialHistory).sexualGender([]);
  const homeEnvironment = oc(socialHistory).homeEnvironment([]);
  const employmentSchool = oc(socialHistory).employmentSchool([]);

  const replyBody = createReplyFromHistory(
    alcohol,
    tobacco,
    substanceUse,
    sexualGender,
    homeEnvironment,
    employmentSchool
  );

  const params = {
    subject: 'Medical History',
    readonly: replyBody,
    transmitParams: {
      source: TransmitSources.SocialHistory,
      payload: socialHistory,
      returnBackTo
    }
  };

  if (isShare) {
    dispatch(messageShare(params));
  } else {
    dispatch(messageReply(params, oc(consumerPcpList)[0].cernerPersonnelId('')));
  }
};

/**
 * Share Social History
 */
export const SHARE_SOCIAL_HISTORY = 'SOCIAL_HISTORY.SHARE_SOCIAL_HISTORY';
export const SHARE_SOCIAL_HISTORY_SUCCESS = 'SOCIAL_HISTORY.SHARE_SOCIAL_HISTORY_SUCCESS';
export const SHARE_SOCIAL_HISTORY_FAIL = 'SOCIAL_HISTORY.SHARE_SOCIAL_HISTORY_FAIL';

export const shareSocialHistory = (
  socialHistory: any,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => ({
  type: SHARE_SOCIAL_HISTORY,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getEndpoint(DispatcherEndpoint.TRANSMIT),
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

/**
 * Download Social History
 */
export const DOWNLOAD_SOCIAL_HISTORY = 'SOCIAL_HISTORY.DOWNLOAD_SOCIAL_HISTORY';
export const DOWNLOAD_SOCIAL_HISTORY_SUCCESS = 'SOCIAL_HISTORY.DOWNLOAD_SOCIAL_HISTORY_SUCCESS';
export const DOWNLOAD_SOCIAL_HISTORY_FAIL = 'SOCIAL_HISTORY.DOWNLOAD_SOCIAL_HISTORY_FAIL';

export const downloadSocialHistory = () => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();

  // Check for in flight downloads if there are any then return
  if (socialHistoryDownloadingSelector(state) === true) {
    return;
  }

  dispatch({ type: DOWNLOAD_SOCIAL_HISTORY, payload: {} });

  const timestamp = Date.now();

  try {
    const uri = `${getEndpoint(DispatcherEndpoint.DOWNLOAD, true)}`;

    const headers: DFDHeadersObject = {
      [DFDHeaders.mhpAccept]: 'application/pdf',
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, uri)
    };

    const response = await FileServices.download(`social-history-${timestamp}.pdf`, uri, headers);

    dispatch({ type: DOWNLOAD_SOCIAL_HISTORY_SUCCESS, payload: { response } });
  } catch (err) {
    dispatch({ type: DOWNLOAD_SOCIAL_HISTORY_FAIL, error: err });
  }
};
